import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Image, Info, Package, Phone, FileText,
  Mail, Upload, Settings, Users, LogOut, Menu, X, ExternalLink,
  Crown, Newspaper
} from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';

const navItems = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/hero', label: 'Hero Section', icon: Image },
  { path: '/admin/about', label: 'About Section', icon: Info },
  { path: '/admin/products', label: 'Products', icon: Package },
  { path: '/admin/contact', label: 'Contact Info', icon: Phone },
  { path: '/admin/footer', label: 'Footer & Menu', icon: FileText },
  { path: '/admin/news', label: 'News', icon: Newspaper },
  { path: '/admin/messages', label: 'Messages', icon: Mail, badge: true },
  { path: '/admin/media', label: 'Media Library', icon: Upload },
  { path: '/admin/settings', label: 'Settings', icon: Settings },
  { path: '/admin/users', label: 'Users', icon: Users, superadminOnly: true },
];

const AdminLayout = ({ children, unreadCount = 0 }) => {
  const { logout, getUser } = useAdminAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const user = getUser();
  const isSuperadmin = user?.role === 'superadmin';

  const visibleItems = navItems.filter(item => !item.superadminOnly || isSuperadmin);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-[rgba(212,175,55,0.15)]">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#D4AF37] rounded-sm flex items-center justify-center">
            <Crown className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-[#D4AF37] font-bold text-sm tracking-wider font-display">ROYAL</p>
            <p className="text-white/40 text-[10px] tracking-widest uppercase">Admin Panel</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {visibleItems.map(item => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-200 group ${
                active
                  ? 'bg-[rgba(212,175,55,0.15)] text-[#D4AF37] border border-[rgba(212,175,55,0.3)]'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-[#D4AF37]' : 'text-white/40 group-hover:text-white/70'}`} />
              <span className="flex-1">{item.label}</span>
              {item.badge && unreadCount > 0 && (
                <span className="bg-[#D4AF37] text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="p-4 border-t border-[rgba(212,175,55,0.15)] space-y-2">
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-3 py-2 text-sm text-white/50 hover:text-white transition-colors rounded-sm hover:bg-white/5"
        >
          <ExternalLink className="w-4 h-4" />
          <span>View Website</span>
        </a>
        <div className="flex items-center justify-between px-3 py-2">
          <div>
            <p className="text-white/80 text-xs font-medium">{user?.username}</p>
            <p className="text-white/40 text-[10px] capitalize">{user?.role}</p>
          </div>
          <button
            onClick={logout}
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-400/10 rounded-sm transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#0A0A0A] text-white overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#111111] border-r border-[rgba(212,175,55,0.1)] flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/70" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-[#111111] border-r border-[rgba(212,175,55,0.1)]">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-[#111111] border-b border-[rgba(212,175,55,0.1)]">
          <button onClick={() => setMobileOpen(true)} className="text-white/70 hover:text-white">
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-sm tracking-wider font-display">ROYAL ADMIN</span>
          </div>
          <div className="w-5" />
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
